import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type RecurrenceBulkScope,
  useDeleteBulkRecurrence,
  usePreviewBulkRecurrence,
} from '@/entities/recurrence';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { Button, Dialog, ErrorState, LoadingState } from '@/shared/ui';

import styles from './Recurrence.module.css';

interface Props {
  sourceCardId: string;
  scope: RecurrenceBulkScope;
  originalDate?: string;
  onClose: () => void;
  onDone: () => void;
}

export function BulkRecurrenceDeleteDialog({
  sourceCardId,
  scope,
  originalDate,
  onClose,
  onDone,
}: Props) {
  const { t, i18n } = useTranslation();
  const [stale, setStale] = useState(false);

  const preview = usePreviewBulkRecurrence(sourceCardId);
  const remove = useDeleteBulkRecurrence(sourceCardId);

  const loadPreview = preview.mutate;

  useEffect(() => {
    loadPreview(scope === 'following' ? { scope, original_date: originalDate } : { scope });
  }, [loadPreview, scope, originalDate]);

  const data = preview.isSuccess ? preview.data : null;
  const date = (value: string) => formatCalendarDate(value, i18n.language);

  const close = () => {
    if (!remove.isPending) onClose();
  };

  const confirm = () => {
    if (!data || remove.isPending) return;

    remove.mutate(
      {
        scope,
        ...(scope === 'following' && {
          original_date: originalDate,
        }),
        expected_version: data.rule_version,
        expected_from_date: data.from_date,
        expected_materialized_cards: data.materialized_cards,
        expected_exceptions: data.exceptions,
      },
      {
        onSuccess: onDone,
        onError: (error) => {
          if (isApiError(error) && error.status === 409) {
            // The previous confirmation is no longer valid.
            // Obtain a fresh preview; never retry deletion automatically.
            setStale(true);
            remove.reset();
            loadPreview(scope === 'following' ? { scope, original_date: originalDate } : { scope });
          }
        },
      },
    );
  };

  return (
    <Dialog title={t('recurrence.bulk.title')} onClose={close} size="lg">
      <div className={styles.policy} data-testid="recurrence-bulk-delete">
        <p className={styles.note}>
          {t(scope === 'following' ? 'recurrence.bulk.following' : 'recurrence.bulk.series')}
        </p>

        {stale && (
          <p className={styles.warn} role="alert">
            {t('recurrence.bulk.stale')}
          </p>
        )}

        {preview.isPending && <LoadingState />}

        {preview.isError && (
          <ErrorState
            error={preview.error}
            onRetry={() => {
              loadPreview(
                scope === 'following' ? { scope, original_date: originalDate } : { scope },
              );
            }}
          />
        )}

        {data && (
          <div className={styles.policy} data-testid="recurrence-bulk-preview">
            <p>
              <strong>
                {t('recurrence.bulk.from', {
                  date: date(data.from_date),
                })}
              </strong>
            </p>

            <p>
              {t('recurrence.bulk.cards', {
                count: data.materialized_cards,
              })}
            </p>

            <p>
              {t('recurrence.bulk.exceptions', {
                count: data.exceptions,
              })}
            </p>

            {data.next_schedule_dates.length > 0 && (
              <div>
                <p className={styles.note}>{t('recurrence.bulk.dates')}</p>
                <ul className={styles.list}>
                  {data.next_schedule_dates.map((item) => (
                    <li key={item}>{date(item)}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.no_explicit_end && data.rule_status !== 'ended' && (
              <p className={styles.warn}>{t('recurrence.bulk.unbounded')}</p>
            )}

            <p className={styles.warn}>{t('recurrence.bulk.notice')}</p>
          </div>
        )}

        {!stale && <FormError error={remove.error} />}

        <div className={styles.actions}>
          <Button onClick={close} disabled={remove.isPending}>
            {t('common.cancel')}
          </Button>

          <Button variant="danger" disabled={!data || remove.isPending} onClick={confirm}>
            {t('recurrence.bulk.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
